import { Component, Injector, OnDestroy } from '@angular/core';
import { ThemeService } from './appServices/theme.service';
import { ModalController, NavController, Platform } from '@ionic/angular';
import { RestaurantBasicInfoStorage } from './appStorage/restaurant-basic-info.storage';
import { KioskResolver } from './pages/kiosk-code/service/kiosk.resolver';
import { StoreResolver } from './appStorage/store.resolver';
import { BnNgIdleService } from 'bn-ng-idle';
import { NavigationEnd, Router } from '@angular/router';
import { skipInactivityUrls } from './appConstants/skipInactivityUrls';
import { UserInactivityComponent } from './includes/user-inactivity/user-inactivity.component';
import { TranslationService } from './appServices/translation.service';
import { ToasterService } from './appServices/toaster.service';
import { InactivityControlService } from './appServices/inactivity-control.service';
import { LogService } from './appServices/log.service';
import { StorageService } from './appServices/storage.service';
import { StorageKeys } from './appServices/storage-keys';
import { App } from '@capacitor/app';
import { MenuResolver } from './pages/menu/service/menu.resolver';
import { ReceiptPrinterService } from './appUtilities/printer/receipt-printer.service';
import { Subscription, firstValueFrom } from 'rxjs';
import { PrintOutboxService } from './appServices/print-outbox.service';
import { CashOrderOperationService } from './appServices/cash-order-operation.service';
import { ConfirmedOrderHandoffService } from './appServices/confirmed-order-handoff.service';
import { CheckoutHelper } from './pages/checkout/services/checkout.helper';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnDestroy {
  inactivityTime: number = 30;
  inactivityModelOpened = false;
  isConnected = true;
  skipInactivityModel: boolean = false;
  private isValidatingKiosk = false; // Prevent concurrent validation
  private isBootstrapping = false; // Prevent concurrent bootstrap
  private appResumeSubscription: any = null;
  private subs = new Subscription();
  private isInitialLoad = true; // Track if this is the initial app load

  constructor(
    public themeService: ThemeService,
    private navCtrl: NavController,
    private restaurantBasicInfo: RestaurantBasicInfoStorage,
    private kioskResolver: KioskResolver,
    private storeResolver: StoreResolver,
    private toaster: ToasterService,
    private bnIdle: BnNgIdleService,
    private router: Router,
    private modalCtrl: ModalController,
    private injector: Injector,
    private inactivityControl: InactivityControlService,
    private logService: LogService,
    private storageService: StorageService,
    private platform: Platform,
    private menuResolver: MenuResolver,
    private receiptPrinterService: ReceiptPrinterService,
    private printOutbox: PrintOutboxService,
    private cashOperation: CashOrderOperationService,
    private confirmedOrderHandoff: ConfirmedOrderHandoffService,
    private checkoutHelper: CheckoutHelper,
  ) {
    this.checkUrlToSkipInactivityModel();
    this.appItialization();
    this.setupAppResumeListener();
  }

  ngOnDestroy(): void {
    if (this.appResumeSubscription) {
      this.appResumeSubscription.remove();
    }
    this.subs.unsubscribe();
  }

  /**
   * Setup app resume listener
   */
  private setupAppResumeListener(): void {
    if (!this.platform.is('capacitor')) {
      return; // Only on native platforms
    }

    this.appResumeSubscription = App.addListener(
      'appStateChange',
      async (state) => {
        if (state.isActive) {
          const currentUrl = this.router.url;
          const isCheckoutFlow =
            currentUrl.startsWith('/checkout') ||
            currentUrl.startsWith('/thanks');

          if (isCheckoutFlow) {
            return;
          }

          // App came to foreground
          console.log('App resumed - rehydrating...');

          // First bootstrap (load data and navigate if needed)
          // bootstrapKiosk() will handle navigation to /home if on /kiosk-code
          await this.bootstrapKiosk();

          // Then validate and navigate if needed (for other scenarios)
          await this.validateKiosk();
        }
      },
    );
  }

  /**
   * Bootstrap kiosk - rehydrates app state on start and resume
   */
  async bootstrapKiosk(): Promise<void> {
    // Prevent concurrent bootstrap calls
    if (this.isBootstrapping) {
      console.log('Bootstrap already in progress, skipping...');
      return;
    }

    this.isBootstrapping = true;

    try {
      console.log('Starting kiosk bootstrap...');

      // 1. Load kiosk config from Preferences
      const tenantName = await this.storageService.getItem<string>(
        StorageKeys.TENANT_NAME,
      );
      const kioskCode = await this.storageService.getItem<string>(
        StorageKeys.KIOSK_CODE,
      );

      // CRITICAL: Verify kiosk code is valid before proceeding
      if (!tenantName || !kioskCode) {
        console.log('No kiosk config found, skipping bootstrap');
        // CRITICAL: Clear any stale in-memory data
        this.kioskResolver.kioskDetails.next(null);
        return;
      }

      const kioskCodeTrimmed = kioskCode.trim();
      if (
        kioskCodeTrimmed.toLowerCase() === 'null' ||
        kioskCodeTrimmed === ''
      ) {
        console.log('Invalid kiosk code, skipping bootstrap');
        this.kioskResolver.kioskDetails.next(null);
        return;
      }

      // 2. Validate and reload kiosk details
      const kioskCodeNum = Number(kioskCodeTrimmed);
      if (!Number.isInteger(kioskCodeNum) || kioskCodeNum <= 0) {
        console.log('Invalid kiosk code number, skipping bootstrap');
        this.kioskResolver.kioskDetails.next(null);
        return;
      }

      // Pending local tickets do not depend on the cloud bootstrap. Drain
      // them as soon as the persisted kiosk identity is known, even offline.
      try {
        await this.printOutbox.resumePending();
      } catch (error) {
        console.error('Failed to resume durable print jobs:', error);
      }
      await this.resumeDurablePaymentRoute();

      // Reload kiosk details (this will also reload store)
      // Use firstValueFrom to convert observable to promise so we can await it
      try {
        const res = await firstValueFrom(
          this.kioskResolver.getKioskDetailsByCode(kioskCodeNum),
        );
        this.kioskResolver.kioskDetails.next(res);
        await this.storeResolver.getStore(res.storeId);

        // CRITICAL: If we're on /kiosk-code page, navigate to /home immediately
        // This prevents the login page from showing when app resumes
        const currentUrl = this.router.url;
        if (currentUrl === '/kiosk-code') {
          console.log(
            'Bootstrap: Found valid kiosk on login page - navigating to home',
          );
          this.navCtrl.navigateForward('/home', { replaceUrl: true });
          // Continue with other bootstrap tasks in background
        }

        // 3. Load menu from IndexedDB (will fallback to API if needed)
        try {
          await firstValueFrom(this.menuResolver.getMenu(false));
          console.log('Menu rehydrated');
        } catch (error) {
          console.error('Failed to rehydrate menu:', error);
        }

        // 4. Reconnect printers
        try {
          await this.reconnectPrinters();
        } catch (error) {
          console.error('Failed to reconnect printers:', error);
        }

        // 5. Stripe reader is automatically reconnected when needed
        // (it loads readerId from storage in getDevice())

        console.log('Kiosk bootstrap completed');
      } catch (err) {
        console.error('Failed to reload kiosk details:', err);
        // CRITICAL: Clear in-memory data on error
        this.kioskResolver.kioskDetails.next(null);
      }
    } catch (error) {
      console.error('Bootstrap error:', error);
      // CRITICAL: Clear in-memory data on error
      this.kioskResolver.kioskDetails.next(null);
    } finally {
      this.isBootstrapping = false;
    }
  }

  private async resumeDurablePaymentRoute(): Promise<void> {
    try {
      const [cash, confirmedOrder] = await Promise.all([
        this.cashOperation.getActive(),
        this.confirmedOrderHandoff.getPending(),
      ]);
      if (!cash && !confirmedOrder) {
        return;
      }
      if (confirmedOrder) {
        // The cart is intentionally already empty after backend ACK. Restore
        // the confirmed order directly instead of routing through checkout,
        // whose empty-cart guard would redirect to /menu before recovery.
        this.checkoutHelper.activeOrderId.next(confirmedOrder.orderId);
        if (!this.router.url.startsWith('/thanks')) {
          await this.router.navigateByUrl('/thanks', { replaceUrl: true });
        }
        return;
      }
      if (!this.router.url.startsWith('/checkout')) {
        await this.router.navigateByUrl('/checkout', { replaceUrl: true });
      }
    } catch (error) {
      // Never erase or bypass a payment journal merely to continue startup.
      console.error('Durable payment recovery requires attention:', error);
    }
  }

  /**
   * Reconnect printers from storage
   */
  private async reconnectPrinters(): Promise<void> {
    try {
      const printers = await this.storageService.getItem<any[]>(
        StorageKeys.PRINTERS,
      );

      if (!printers || !Array.isArray(printers)) {
        return;
      }

      // Reconnect only auto-print printers that were previously connected
      const autoPrinters = printers.filter(
        (p: any) => p.autoPrint === true && p.connected === true,
      );

      if (autoPrinters.length > 0) {
        // Do NOT call getAllAutoPrinters() here: it PRINTS on every connected
        // auto-print printer, and with no active order that meant a dummy
        // ticket on each app resume. Printer config lives in Preferences and
        // is read at print time; nothing to do on resume.
        console.log(
          `${autoPrinters.length} auto-print printer(s) configured; reconnection happens at print time.`,
        );
      }
    } catch (error) {
      console.error('Failed to reconnect printers:', error);
    }
  }

  /**
   * Initialize app
   * call required services
   */
  async appItialization(): Promise<void> {
    try {
      // Wait for translation service to initialize first (loads language from storage)
      const translationService = this.injector.get(TranslationService);
      await translationService.waitForInitialization();

      // Verify translations are actually loaded
      if (!translationService.areTranslationsLoaded()) {
        console.warn(
          'Translations not loaded after initialization wait - this may cause translation keys to display',
        );
      }

      // CRITICAL: Validate kiosk FIRST before any bootstrap
      // This ensures we navigate to /kiosk-code if no kiosk is found
      // Mark as initial load - this allows validation to run even if on /kiosk-code
      this.isInitialLoad = true;
      await this.validateKiosk();
      this.isInitialLoad = false; // Mark initial load as complete

      // CRITICAL: Only bootstrap if we have a valid kiosk code
      // Check storage again to ensure kiosk code exists before bootstrapping
      const tenantName = await this.storageService.getItem<string>(
        StorageKeys.TENANT_NAME,
      );
      const kioskCode = await this.storageService.getItem<string>(
        StorageKeys.KIOSK_CODE,
      );

      if (
        tenantName &&
        kioskCode &&
        kioskCode.trim().toLowerCase() !== 'null'
      ) {
        const kioskCodeNum = Number(kioskCode.trim());
        if (Number.isInteger(kioskCodeNum) && kioskCodeNum > 0) {
          // Only bootstrap if we have valid kiosk code
          await this.bootstrapKiosk();
        }
      }
    } catch (error) {
      console.error('Error during app initialization:', error);
      // Still try to validate kiosk even if translation fails
      this.validateKiosk().catch((err) => {
        console.error('Error during kiosk validation:', err);
      });
    }

    this.checkUserInactivity();
    // Update checks stay manual from the settings page to avoid auto-popups on boot.
  }

  checkUrlToSkipInactivityModel() {
    this.router.events.subscribe((event: any) => {
      if (event instanceof NavigationEnd) {
        if (skipInactivityUrls.includes(this.router.url)) {
          this.skipInactivityModel = true;
        } else {
          this.skipInactivityModel = false;
        }
      }
    });
  }

  checkUserInactivity() {
    this.bnIdle
      .startWatching(this.inactivityTime)
      .subscribe((isTimedOut: boolean) => {
        if (
          isTimedOut &&
          !this.inactivityModelOpened &&
          !this.skipInactivityModel &&
          this.isConnected &&
          !this.inactivityControl.pauseInactivityModal$.value
        ) {
          this.inactivityModelOpened = true;
          this.openInactivityModel();
        }
      });
  }

  async openInactivityModel() {
    const modal = await this.modalCtrl.create({
      component: UserInactivityComponent,
      mode: 'md',
      cssClass: 'rounded-modal sml',
      backdropDismiss: false,
    });
    await modal.present();
    const { role, data } = await modal.onWillDismiss();
    if (data) {
      this.inactivityModelOpened = false;
    }
  }

  /**
   * validate kiosk
   * @returns
   */
  async validateKiosk() {
    // Prevent concurrent validation
    if (this.isValidatingKiosk) {
      return;
    }

    // CRITICAL FIX: If on /home and kiosk details already loaded, skip validation completely
    // This prevents validateKiosk() from interfering with admin-validate navigation to /settings
    const currentUrl = this.router.url;
    if (currentUrl === '/home') {
      const existingDetails = this.kioskResolver.kioskDetails.value;
      if (existingDetails) {
        // Already on /home with kiosk details loaded - no need to validate
        return;
      }
    }

    // Skip validation if on settings page
    if (currentUrl === '/settings') {
      return;
    }

    // For /kiosk-code: Allow validation if initial load OR if kiosk details are already loaded
    // This allows:
    // 1. Initial validation to work
    // 2. App resume to navigate from /kiosk-code to /home if kiosk exists
    // But skip if form submission is in progress (no kiosk details loaded yet)
    if (
      currentUrl === '/kiosk-code' &&
      !this.isInitialLoad &&
      !this.kioskResolver.kioskDetails.value
    ) {
      // On /kiosk-code, not initial load, and no kiosk details = form submission in progress
      // Let the page handle its own navigation
      return;
    }

    this.isValidatingKiosk = true;

    try {
      // Use async storage operations (Capacitor Preferences)
      const rawTenant = await this.storageService.getItem<string>(
        StorageKeys.TENANT_NAME,
      );
      const rawKiosk = await this.storageService.getItem<string>(
        StorageKeys.KIOSK_CODE,
      );

      // CRITICAL: Re-check URL after async storage read (navigation might have started)
      // Skip if we're on /home, /settings, or /kiosk-code
      const urlAfterStorage = this.router.url;
      if (
        urlAfterStorage === '/home' ||
        urlAfterStorage === '/settings' ||
        urlAfterStorage === '/kiosk-code'
      ) {
        return; // Navigation happened or we're already on target page
      }

      const tenantName = rawTenant ? rawTenant.trim() : null;
      const kioskRaw = rawKiosk ? rawKiosk.trim() : null;

      // CRITICAL: Check for 'null' string or empty string
      if (
        !tenantName ||
        !kioskRaw ||
        kioskRaw.toLowerCase() === 'null' ||
        kioskRaw === ''
      ) {
        // CRITICAL: Clear any stale in-memory data
        this.kioskResolver.kioskDetails.next(null);

        if (
          this.router.url !== '/settings' &&
          this.router.url !== '/kiosk-code'
        ) {
          // Use replaceUrl to prevent back navigation
          this.router.navigateByUrl('/kiosk-code', { replaceUrl: true });
        }
        return;
      }

      const kioskCode = Number(kioskRaw);
      if (!Number.isInteger(kioskCode) || kioskCode <= 0) {
        // CRITICAL: Clear any stale in-memory data
        this.kioskResolver.kioskDetails.next(null);

        if (
          this.router.url !== '/settings' &&
          this.router.url !== '/kiosk-code'
        ) {
          this.router.navigateByUrl('/kiosk-code', { replaceUrl: true });
        }
        return;
      }

      // CRITICAL: Call getKioskDetails() if we have valid kiosk code
      // Don't call if we're on /home, /settings, or /kiosk-code
      // When on /kiosk-code, the page handles its own navigation
      const finalUrl = this.router.url;
      if (
        finalUrl !== '/home' &&
        finalUrl !== '/settings' &&
        finalUrl !== '/kiosk-code'
      ) {
        this.getKioskDetails(kioskCode);
      }
    } finally {
      this.isValidatingKiosk = false;
    }
  }

  /**
   * get kiosk details
   * @param kioskCode
   */
  getKioskDetails(kioskCode: number) {
    // CRITICAL FIX: If on /home, load data but NEVER navigate
    const currentUrl = this.router.url;
    if (currentUrl === '/home') {
      // If details already loaded with correct kiosk code, do nothing
      const existingDetails = this.kioskResolver.kioskDetails.value;
      if (existingDetails && existingDetails.kioskCode === kioskCode) {
        return; // Already have correct details - do nothing
      }
      // If on /home but details not loaded or wrong, load them but DON'T navigate
      this.kioskResolver.getKioskDetailsByCode(kioskCode).subscribe(
        async (res) => {
          this.kioskResolver.kioskDetails.next(res);
          await this.storeResolver.getStore(res.storeId);
          // NO NAVIGATION - we're already on /home
        },
        (err) => {
          console.error('Error loading kiosk details:', err);
        },
      );
      return; // Exit early - don't proceed with navigation logic
    }

    // Skip if on settings
    if (currentUrl === '/settings') {
      return;
    }

    // For /kiosk-code: Allow navigation if kiosk details are already loaded (app resume scenario)
    // But skip if no kiosk details (form submission in progress)
    if (
      currentUrl === '/kiosk-code' &&
      !this.kioskResolver.kioskDetails.value
    ) {
      // No kiosk details = form submission in progress, let page handle navigation
      return;
    }

    this.kioskResolver.getKioskDetailsByCode(kioskCode).subscribe(
      async (res) => {
        // CRITICAL: Check URL immediately
        // If on /settings, don't navigate
        // If on /kiosk-code but kiosk details already loaded, navigate to /home (app resume scenario)
        if (this.router.url === '/settings') {
          // Update details but don't navigate away from settings
          this.kioskResolver.kioskDetails.next(res);
          await this.storeResolver.getStore(res.storeId);
          return;
        }

        // If on /kiosk-code and we have kiosk details, navigate to /home (app resume)
        if (
          this.router.url === '/kiosk-code' &&
          this.kioskResolver.kioskDetails.value
        ) {
          // Kiosk details already loaded = app resume scenario, navigate to /home
          this.kioskResolver.kioskDetails.next(res);
          await this.storeResolver.getStore(res.storeId);
          // Navigate to home after a short delay
          await new Promise((resolve) => setTimeout(resolve, 100));
          if (this.router.url === '/kiosk-code') {
            this.navCtrl.navigateForward('/home');
          }
          return;
        }

        // If already on /home, just update details
        if (this.router.url === '/home') {
          this.kioskResolver.kioskDetails.next(res);
          await this.storeResolver.getStore(res.storeId);
          return;
        }

        this.kioskResolver.kioskDetails.next(res);
        await this.storeResolver.getStore(res.storeId);

        // CRITICAL: Check URL multiple times with delays to catch navigation
        for (let i = 0; i < 20; i++) {
          const checkUrl = this.router.url;
          if (
            checkUrl === '/settings' ||
            checkUrl === '/home' ||
            checkUrl === '/kiosk-code'
          ) {
            return; // Exit if we're on settings, home, or kiosk-code
          }
          await new Promise((resolve) => setTimeout(resolve, 50));
        }

        // Final check before navigation
        // Navigate to /home only if not on /settings or /kiosk-code
        const finalUrl = this.router.url;
        if (
          finalUrl !== '/settings' &&
          finalUrl !== '/home' &&
          finalUrl !== '/kiosk-code'
        ) {
          this.navCtrl.navigateForward('/home');
        }
      },
      (err) => {
        const translate = this.injector.get(TranslationService);
        this.toaster.errorToastWithClose(
          translate.translate('KIOSK_CODE_INVALID'),
        );

        if (
          this.router.url !== '/settings' &&
          this.router.url !== '/kiosk-code'
        ) {
          this.router.navigateByUrl('/kiosk-code', { replaceUrl: true });
        }
      },
    );
  }

  /**
   * Save error logs
   * @param message
   */
  saveErrorLogs(message: any) {
    message.channel = 'Kiosk';
    const payload = {
      type: 'Frontend',
      logs: message,
    };
    this.logService.saveLogs(payload).subscribe({
      next: (res) => {},
      error: (err) => {},
    });
  }

  // connectToPrintAgent() {
  //   console.log('Connect To Print Agent');
  //   const ws = new WebSocket('ws://127.0.0.1:1819/events');

  //   ws.onmessage = (msg) => {
  //     const event = JSON.parse(msg.data);
  //     console.log('Print event:', event);

  //     if (event.type === 'PRINT_STATUS') {
  //       this.handlePrintStatus(event);
  //     }
  //   };

  //   ws.onclose = () => {
  //     console.warn('WS disconnected, retrying...');
  //     setTimeout(() => this.connectToPrintAgent(), 3000);
  //   };
  // }

  // handlePrintStatus(e: any) {
  //   if (e.status === 'PRINTED') {
  //     console.log('Printed successfully');
  //   }
  //   if (e.status === 'QUEUED') {
  //     console.log('Printer offline');
  //   }
  //   if (e.status === 'FAILED') {
  //     console.log('Print failed: ' + e.error);
  //   }
  // }
}
